import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { MessagesSquare, PlusCircle } from "lucide-react";

/** Eén extra vraag bij dit dier — spiegelt api {@code ExtraVraagResponse}. */
interface ExtraVraag {
  id: string;
  vraag: string;
  verplicht: boolean;
}

/**
 * De vragen die dit asiel bij dít dier stelt, bovenop de lijst van zijn diersoort.
 *
 * <p><b>Waarom een staartje en geen eigen lijst.</b> Een vragenlijst per dier is een lijst die
 * niemand onderhoudt: zeshonderd dieren worden zeshonderd formulieren die uit elkaar lopen. Maar bij
 * één dier is er soms precies één ding dat je wilt weten voordat iemand langskomt — een omheinde
 * tuin bij een graver, of je overdag thuis bent bij een hond met scheidingsangst. Dat hoort achter
 * de vaste vragen, in een eigen blok met de naam van het dier erboven.
 *
 * <p>Ze reizen mee met de publicatie: opslaan stuurt de advertentie opnieuw naar DierMatch, zodat
 * het formulier op de site meteen klopt. Staat het dier nergens op, dan worden ze bewaard en gaan ze
 * mee zodra hij wél gepubliceerd wordt.
 */
export function KennismakingExtraVragen({ advertentieId }: { advertentieId: string }) {
  const t = useTranslate();
  const { data, isLoading, refetch } = useCustom<{ vragen: ExtraVraag[] }>({
    url: `herplaatsing/${advertentieId}/extra-vragen`,
    method: "get",
  });
  const { mutate, isLoading: bezig } = useCustomMutation();

  const [vragen, setVragen] = useState<ExtraVraag[]>([]);
  const [nieuw, setNieuw] = useState("");
  const [bewaard, setBewaard] = useState(false);

  useEffect(() => {
    setVragen(data?.data.vragen ?? []);
  }, [data]);

  const opslaan = (lijst: ExtraVraag[]) => {
    setBewaard(false);
    mutate(
      {
        url: `herplaatsing/${advertentieId}/extra-vragen`,
        method: "post",
        values: { vragen: lijst.map((v) => ({ vraag: v.vraag, verplicht: v.verplicht })) },
      },
      {
        onSuccess: () => {
          setBewaard(true);
          void refetch();
        },
      },
    );
  };

  const toevoegen = () => {
    const tekst = nieuw.trim();
    if (tekst === "") return;
    const lijst = [...vragen, { id: `nieuw-${vragen.length + 1}`, vraag: tekst, verplicht: false }];
    setVragen(lijst);
    setNieuw("");
    opslaan(lijst);
  };

  const weghalen = (id: string) => {
    const lijst = vragen.filter((v) => v.id !== id);
    setVragen(lijst);
    opslaan(lijst);
  };

  const omzetten = (id: string) => {
    const lijst = vragen.map((v) => (v.id === id ? { ...v, verplicht: !v.verplicht } : v));
    setVragen(lijst);
    opslaan(lijst);
  };

  return (
    <section className="fawkes-kennis-extra">
      <span className="fawkes-kennis-extra__icoon" aria-hidden="true">
        <MessagesSquare size={20} />
      </span>
      <div className="fawkes-kennis-extra__body">
      <h3 className="fawkes-kennis-extra__kop">{t("kennismaking.extra.titel")}</h3>
      <p className="fawkes-kennis-extra__uitleg">{t("kennismaking.extra.uitleg")}</p>

      {isLoading ? (
        <p className="fawkes-kennis__leeg">{t("common.loading")}</p>
      ) : (
        <>
          {vragen.length > 0 && (
            <ul className="fawkes-kennis-extra__lijst">
              {vragen.map((v) => (
                <li key={v.id} className="fawkes-kennis-extra__rij">
                  <span className="fawkes-kennis-extra__vraag">{v.vraag}</span>
                  <label className="fawkes-kennis-extra__verplicht">
                    <input
                      type="checkbox"
                      checked={v.verplicht}
                      disabled={bezig}
                      onChange={() => omzetten(v.id)}
                    />
                    {t("kennismaking.extra.verplicht")}
                  </label>
                  <Button variant="secondary" disabled={bezig} onClick={() => weghalen(v.id)}>
                    {t("common.delete")}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="fawkes-kennis-extra__nieuw">
            <input
              type="text"
              className="fawkes-kennis-extra__invoer"
              placeholder={t("kennismaking.extra.plaatshouder")}
              value={nieuw}
              disabled={bezig}
              onChange={(e) => setNieuw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  toevoegen();
                }
              }}
            />
            <Button disabled={bezig || nieuw.trim() === ""} onClick={toevoegen}>
              <PlusCircle size={16} aria-hidden="true" />
              {t("kennismaking.extra.toevoegen")}
            </Button>
          </div>

          {bewaard && (
            <p className="fawkes-kennis-extra__bewaard">{t("kennismaking.extra.bewaard")}</p>
          )}
        </>
      )}
      </div>
    </section>
  );
}
